import { useState, type ReactNode } from "react";
import { NavLink } from "react-router";
import { ApiError, errorMessage } from "../api";
import { ago, dateTime, json, money, play, shortId } from "../format";
import { useToast } from "./toast";

// ---------------------------------------------------------------- layout

export function Page({ title, sub, actions, crumbs, children }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; crumbs?: ReactNode; children: ReactNode }) {
  return (
    <div className="page">
      <div className="page-head">
        <div className="titles">
          {crumbs && <div className="crumbs">{crumbs}</div>}
          <h1>{title}</h1>
          {sub && <div className="sub">{sub}</div>}
        </div>
        {actions && <div className="actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Card({ title, actions, children, flush, className, testid }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean; className?: string; testid?: string }) {
  return (
    <section className={"card" + (flush ? " flush" : "") + (className ? " " + className : "")} data-testid={testid}>
      {(title || actions) && (
        <div className="card-head">
          <h2>{title}</h2>
          {actions}
        </div>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

export function Tabs({ tabs }: { tabs: { to: string; label: ReactNode; end?: boolean; hidden?: boolean }[] }) {
  return (
    <nav className="tabs" aria-label="Sections">
      {tabs
        .filter((t) => !t.hidden)
        .map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => (isActive ? "active" : "")}>
            {t.label}
          </NavLink>
        ))}
    </nav>
  );
}

/** In-page tabs driven by state (no URL). */
export function StateTabs<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[] }) {
  return (
    <div className="tabs" role="tablist">
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Seg<T extends string>({ value, onChange, options, label, disabled, title }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[]; label?: string; disabled?: boolean; title?: string }) {
  return (
    <div className="seg" role="group" aria-label={label} title={title}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} disabled={disabled} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Field({ label, hint, error, children, className }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={"field" + (className ? " " + className : "")}>
      <span className="label">{label}</span>
      {children}
      {error ? <span className="err">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

/** Like Field, but for groups of buttons (chips, segmented controls): a div, not a <label>. */
export function Group({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="field" role="group" aria-label={typeof label === "string" ? label : undefined}>
      <span className="label">{label}</span>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- states

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="state" aria-busy="true">
      <span className="spinner" /> <span style={{ marginLeft: 6 }}>{label}</span>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <div className="state error" role="alert">
      <div className="state-title">{forbidden ? "Not allowed" : "Couldn't load this"}</div>
      <div>{errorMessage(error)}</div>
      {retry && !forbidden && (
        <div style={{ marginTop: 8 }}>
          <button className="small" onClick={retry}>
            Try again
          </button>
        </div>
      )}
    </div>
  );
}

export function Empty({ title = "Nothing here", children }: { title?: ReactNode; children?: ReactNode }) {
  return (
    <div className="state">
      <div className="state-title">{title}</div>
      {children && <div>{children}</div>}
    </div>
  );
}

/** Renders loading / error / content for a react-query result. */
export function QueryView<T>({ q, children }: { q: { data?: T; isLoading: boolean; error: unknown; refetch: () => unknown }; children: (d: T) => ReactNode }) {
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  if (q.data === undefined) return <Loading />;
  return <>{children(q.data)}</>;
}

export function Callout({ kind = "info", children }: { kind?: "info" | "ok" | "warn" | "danger"; children: ReactNode }) {
  return <div className={"callout " + kind}>{children}</div>;
}

// ---------------------------------------------------------------- data display

const STATUS_KIND: Record<string, string> = {
  done: "ok", executed: "ok", granted: "ok", paid: "ok", active: "ok", published: "ok", matched: "ok", won: "ok", resolved: "ok",
  approved: "ok", ok: "ok", live: "ok", actioned: "ok", checked_in: "ok", verified: "ok",
  submitted: "violet",
  pending: "warn", invited: "warn", created: "warn", review: "warn", open: "warn", evidence_submitted: "warn", scheduled: "warn", held: "warn",
  suspended: "warn", paused: "warn", processing: "warn",
  pending_approval: "violet",
  failed: "danger", rejected: "danger", banned: "danger", charged_back: "danger", dead: "danger", lost: "danger", missing_order: "danger",
  amount_mismatch: "danger", deleted: "danger", overdue: "danger", expired: "danger", accepted: "danger", voided: "danger", dissolved: "danger",
  disabled: "danger",
  refunded: "info", retired: "info", dismissed: "info", caps_lowered: "info", draft: "info", closed: "info", ended: "info", false_positive: "info",
  guest: "", full: "accent",
  visible: "ok", hidden: "warn", cancelled: "info", removed: "danger",
};

export function Badge({ kind, children, title }: { kind?: string; children: ReactNode; title?: string }) {
  return (
    <span className={"badge " + (kind || "")} title={title}>
      {children}
    </span>
  );
}

export function Status({ s }: { s: unknown }) {
  if (s === null || s === undefined || s === "") return <span className="muted">—</span>;
  const v = String(s);
  return <Badge kind={STATUS_KIND[v] ?? ""}>{v.replace(/_/g, " ")}</Badge>;
}

export function Bool({ v, yes = "yes", no = "no" }: { v: unknown; yes?: string; no?: string }) {
  if (v === null || v === undefined) return <span className="muted">—</span>;
  return v ? <Badge kind="ok">{yes}</Badge> : <span className="muted">{no}</span>;
}

export function Time({ iso, rel }: { iso: unknown; rel?: boolean }) {
  if (!iso || typeof iso !== "string") return <span className="muted">—</span>;
  return (
    <time dateTime={iso} title={iso}>
      {rel ? ago(iso) : dateTime(iso)}
    </time>
  );
}

export function Money({ minor, currency }: { minor: unknown; currency: unknown }) {
  if (minor === null || minor === undefined) return <span className="muted">—</span>;
  return <span className="nowrap">{money(Number(minor), String(currency ?? ""))}</span>;
}

export function Play({ n, symbol, signed }: { n: unknown; symbol?: string; signed?: boolean }) {
  if (n === null || n === undefined) return <span className="muted">—</span>;
  const v = Number(n);
  const s = play(Math.abs(v), symbol);
  if (!signed) return <span className="nowrap">{v < 0 ? "−" + s : s}</span>;
  return <span className={"nowrap " + (v > 0 ? "pos" : v < 0 ? "neg" : "")}>{(v > 0 ? "+" : v < 0 ? "−" : "") + s}</span>;
}

export function Id({ id, full, copy }: { id: unknown; full?: boolean; copy?: boolean }) {
  const toast = useToast();
  if (!id || typeof id !== "string") return <span className="muted">—</span>;
  return (
    <span className="nowrap">
      <span className="mono" title={id} style={{ userSelect: "all" }}>
        {full ? id : shortId(id)}
      </span>
      {copy && (
        <button
          type="button"
          className="ghost small"
          style={{ height: 20, padding: "0 4px", marginLeft: 2 }}
          aria-label="Copy id"
          title="Copy"
          onClick={() =>
            navigator.clipboard?.writeText(id).then(
              () => toast("info", "Copied " + shortId(id)),
              () => {},
            )
          }
        >
          ⧉
        </button>
      )}
    </span>
  );
}

export function KV({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: "contents" }}>
          <dt>{k}</dt>
          <dd>{v === null || v === undefined || v === "" ? <span className="muted">—</span> : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function JsonView({ value, tall, collapsed }: { value: unknown; tall?: boolean; collapsed?: boolean }) {
  const [open, setOpen] = useState(!collapsed);
  if (!open)
    return (
      <button className="link small" onClick={() => setOpen(true)}>
        Show JSON
      </button>
    );
  return <pre className={"json" + (tall ? " tall" : "")}>{json(value)}</pre>;
}

export function Tile({ label, value, sub, alert, testid }: { label: ReactNode; value: ReactNode; sub?: ReactNode; alert?: boolean; testid?: string }) {
  return (
    <div className={"tile" + (alert ? " alert" : "")} data-testid={testid}>
      <div className="t-label">{label}</div>
      <div className="t-value">{value}</div>
      {sub && <div className="t-sub">{sub}</div>}
    </div>
  );
}

export function Delta({ now, before }: { now: number; before: number }) {
  if (!before && !now) return <span className="muted">no change</span>;
  if (!before) return <span className="delta-up">new</span>;
  const d = (now - before) / Math.abs(before);
  return (
    <span className={d >= 0 ? "delta-up" : "delta-down"}>
      {d >= 0 ? "▲" : "▼"} {Math.abs(d * 100).toFixed(0)}% vs yesterday
    </span>
  );
}

export function BarCell({ value, max, label, alt, neg }: { value: number; max: number; label: ReactNode; alt?: boolean; neg?: boolean }) {
  const w = max > 0 ? Math.max(1, Math.round((Math.abs(value) / max) * 120)) : 1;
  return (
    <span className="bar-cell">
      <span className={"bar" + (alt ? " alt" : "") + (neg ? " neg" : "")} style={{ width: w }} />
      <span>{label}</span>
    </span>
  );
}

/**
 * A 0–1 share as a short track (e.g. budget spent), with an optional marker
 * for where it should be (e.g. share of the flight elapsed). `tone` colours
 * the fill: ok (on plan), warn, attn.
 */
export function Meter({ value, mark, tone = "ok", label, width = 96, testid }: { value: number; mark?: number; tone?: "ok" | "warn" | "attn" | "muted"; label?: ReactNode; width?: number; testid?: string }) {
  const pc = (v: number) => Math.max(0, Math.min(100, v * 100));
  return (
    <span className="meter-cell" data-testid={testid}>
      <span className={"meter " + tone} style={{ width }} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pc(value))}>
        <span className="meter-fill" style={{ width: pc(value) + "%" }} />
        {mark !== undefined && <span className="meter-mark" style={{ left: pc(mark) + "%" }} />}
      </span>
      {label !== undefined && <span>{label}</span>}
    </span>
  );
}

/** A value that stays hidden until the viewer clicks (for data that is not needed by default). */
export function Masked({ value, mask = "••••••" }: { value: ReactNode; mask?: string }) {
  const [shown, setShown] = useState(false);
  return shown ? (
    <span>{value}</span>
  ) : (
    <button className="link small" onClick={() => setShown(true)} title="Show">
      <span className="masked">{mask}</span>
    </button>
  );
}
